import React, { useMemo, useState } from 'react';

import BoxContainer from '@src/components/BoxContainer';
import ListViewportLayout from '@src/components/ListViewportLayout';
import SecondaryPanel from '@src/components/SecondaryPanel/SecondaryPanel';
import ReportDivisionAccessGate from '@src/packages/dashboard/components/ReportDivisionAccessGate';
import { useServerReportPdf } from '@src/packages/dashboard/hooks/useServerReportPdf';
import { useCostCenterFilterIds } from '@src/packages/costCenters/hooks/useCostCenterFilterIds';

import TasksDashboardDrawers from './components/TasksDashboardDrawers';
import TasksDashboardHeader from './components/TasksDashboardHeader';
import TasksDetail from './components/TasksDetail';
import TasksFilterPanel from './components/TasksFilterPanel';
import TasksReportTabs, { type TasksReportTab } from './components/TasksReportTabs';
import { useTasksDashboard } from './hooks/useTasksDashboard';
import { useTasksSummarySort } from './hooks/useTasksSummarySort';
import styles from './styles.module.less';
import TasksSummary from './TasksSummary';
import { buildHistoryQuery } from './TasksSummary/historyQuery';
import { useExecutionsHistorySections } from './TasksSummary/hooks/useExecutionsHistorySections';
import type { TasksChartViews } from './TasksSummary/types';

export const TasksDashboardContent: React.FC = () => {
  const dashboard = useTasksDashboard();
  const [activeTab, setActiveTab] = useState<TasksReportTab>('summary');
  const [chartViews, setChartViews] = useState<TasksChartViews>({ evolution: 'bar', incidents: 'bar' });
  const summarySort = useTasksSummarySort();
  const onChartViewChange = (chart: keyof TasksChartViews, view: TasksChartViews[typeof chart]) => {
    setChartViews(current => ({ ...current, [chart]: view }));
  };
  const { queryIds: costCenterQueryIds, isExpandReady: costCenterExpandReady } =
    useCostCenterFilterIds(dashboard.chartFilters.costCenterIds);
  // The summary sections live here so the screen and the PDF print the very same data.
  const query = useMemo(
    () =>
      costCenterExpandReady
        ? buildHistoryQuery(
            {
              ...dashboard.chartFilters,
              costCenterIds: costCenterQueryIds ?? [],
            },
            dashboard.orgId,
            dashboard.queryDivisionIds,
            dashboard.timeZone,
          )
        : undefined,
    [
      costCenterExpandReady,
      costCenterQueryIds,
      dashboard.chartFilters,
      dashboard.orgId,
      dashboard.queryDivisionIds,
      dashboard.timeZone,
    ],
  );
  const history = useExecutionsHistorySections(query, {
    enabled: dashboard.canQueryDivisions && Boolean(query),
  });

  const serverPdfAction = useServerReportPdf(
    dashboard.canQueryDivisions && query
      ? {
          ...query,
          reportType: 'tasks',
          variant: 'full',
          presentation: {
            ...chartViews,
            executionSort: summarySort.executionSort,
            complianceSort: summarySort.complianceSort,
          },
        }
      : undefined,
  );

  return (
    <>
      <TasksFilterPanel
        title={dashboard.intl.formatMessage({ id: 'dashboards.chart.filters' })}
        filters={dashboard.filters}
        initialFilters={dashboard.initialFilters}
        chartFilters={dashboard.draftChartFilters}
        orgId={dashboard.orgId}
        divisionIds={dashboard.queryDivisionIds}
        disabledDate={dashboard.disabledDate}
        onFiltersChange={dashboard.setFilters}
        onTemplatesChange={dashboard.setSelectedTemplates}
        onApplyFilters={dashboard.applyFilters}
        hasPendingFilters={dashboard.hasPendingFilters}
        isApplyingFilters={dashboard.mainLoading}
      />
      <TasksDashboardHeader
        intl={dashboard.intl}
        isFilterPanelOpen={dashboard.isOpen}
        onToggleFilters={dashboard.toggle}
        onCustomFilterChange={dashboard.setFilters}
        serverPdfAction={serverPdfAction}
        onRefresh={activeTab === 'summary' ? history.refresh : dashboard.fetch}
        refreshDisabled={!dashboard.canQueryDivisions || (activeTab === 'summary' ? history.loading : dashboard.mainLoading)}
      />
      {/* The summary shows a skeleton per section; only the detail tab waits on this load. */}
      <BoxContainer loading={activeTab === 'detail' && dashboard.mainLoading} fullContent>
        <SecondaryPanel.Content>
          <ListViewportLayout
            viewportOffset={160}
            minHeight="calc(100dvh - 160px)"
            sidePanelOpen={dashboard.isOpen}
            constrainToScrollParent={false}
            top={<TasksReportTabs activeTab={activeTab} onChange={setActiveTab} />}
            topClassName={styles.tabsHeader}
          >
            <ReportDivisionAccessGate
              canQuery={dashboard.canQueryDivisions}
              permissionsLoaded={dashboard.permissionsLoaded}
            >
              {activeTab === 'summary' ? (
                <TasksSummary
                  query={query}
                  chartFilters={dashboard.chartFilters}
                  sections={history.sections}
                  onRetrySection={history.retry}
                  chartViews={chartViews}
                  onChartViewChange={onChartViewChange}
                  {...summarySort}
                />
              ) : (
                <TasksDetail
                  intl={dashboard.intl}
                  tableDataByDay={dashboard.tableDataByDay}
                  groupBy={dashboard.groupBy}
                  columns={dashboard.columns}
                />
              )}
            </ReportDivisionAccessGate>
          </ListViewportLayout>
        </SecondaryPanel.Content>
      </BoxContainer>
      <TasksDashboardDrawers
        isTimelineOpen={dashboard.openTimelineEvents}
        organizationId={dashboard.orgId}
        divisionIds={dashboard.queryDivisionIds}
        onCloseTimeline={dashboard.onBackFromTimeline}
        isEventTypesOpen={dashboard.showEventTypeDrawer}
        onCloseEventTypes={() => dashboard.setShowEventTypeDrawer(false)}
        isUserListOpen={dashboard.drawerState.users}
        onCloseUserList={() => dashboard.toggleDrawer('users')}
        selectedUsers={dashboard.selectedUsers}
      />
    </>
  );
};

const TasksDashboard: React.FC = () => <TasksDashboardContent />;

export default TasksDashboard;
